import type {
  CreateDeviceMappingInput,
  DeviceMapping,
  DeviceMappingListQuery,
  InstallTrackingIntegrationInput,
  Page,
  ReportingDevice,
  ReportingDeviceListQuery,
  SetTruckDeviceInput,
  TestTrackingConnectionInput,
  TrackingAdapterSummary,
  TrackingConnectionResult,
  TrackingDevicesResult,
  TrackingIntegration,
  TrackingIntegrationListQuery,
  UnmappedDevice,
  UnmappedDeviceListQuery,
  UpdateTrackingIntegrationInput,
} from '@chukua/common';
import { api } from '../../../../shared/shared.api';
import { toListParams } from '../../../../shared/policies/list-params';

const json = (body: unknown) => JSON.stringify(body);

export function listAdapters(): Promise<TrackingAdapterSummary[]> {
  return api<TrackingAdapterSummary[]>('/tracking/adapters');
}

export function listIntegrations(query: TrackingIntegrationListQuery): Promise<Page<TrackingIntegration>> {
  return api<Page<TrackingIntegration>>(`/tracking/integrations?${toListParams(query)}`);
}

export function getIntegration(id: string): Promise<TrackingIntegration> {
  return api<TrackingIntegration>(`/tracking/integrations/${id}`);
}

export function testIntegrationConnection(input: TestTrackingConnectionInput): Promise<TrackingConnectionResult> {
  return api<TrackingConnectionResult>('/tracking/integrations/test-connection', { method: 'POST', body: json(input) });
}

export function installIntegration(input: InstallTrackingIntegrationInput): Promise<TrackingIntegration> {
  return api<TrackingIntegration>('/tracking/integrations', { method: 'POST', body: json(input) });
}

export function updateIntegration(id: string, input: UpdateTrackingIntegrationInput): Promise<TrackingIntegration> {
  return api<TrackingIntegration>(`/tracking/integrations/${id}`, { method: 'PATCH', body: json(input) });
}

export function enableIntegration(id: string): Promise<TrackingIntegration> {
  return api<TrackingIntegration>(`/tracking/integrations/${id}/enable`, { method: 'POST' });
}

export function disableIntegration(id: string): Promise<TrackingIntegration> {
  return api<TrackingIntegration>(`/tracking/integrations/${id}/disable`, { method: 'POST' });
}

export function uninstallIntegration(id: string): Promise<TrackingIntegration> {
  return api<TrackingIntegration>(`/tracking/integrations/${id}`, { method: 'DELETE' });
}

export function listIntegrationDevices(id: string, q?: string): Promise<TrackingDevicesResult> {
  return api<TrackingDevicesResult>(`/tracking/integrations/${id}/devices${q ? `?q=${encodeURIComponent(q)}` : ''}`);
}

export function listDeviceMappings(query: DeviceMappingListQuery): Promise<Page<DeviceMapping>> {
  return api<Page<DeviceMapping>>(`/tracking/device-mappings?${toListParams(query)}`);
}

export function createDeviceMapping(input: CreateDeviceMappingInput): Promise<DeviceMapping> {
  return api<DeviceMapping>('/tracking/device-mappings', { method: 'POST', body: json(input) });
}

export function closeDeviceMapping(id: string, effectiveTo: string): Promise<DeviceMapping> {
  return api<DeviceMapping>(`/tracking/device-mappings/${id}`, { method: 'PATCH', body: json({ effectiveTo }) });
}

export function listReportingDevices(query: ReportingDeviceListQuery): Promise<Page<ReportingDevice>> {
  return api<Page<ReportingDevice>>(`/tracking/reporting-devices?${toListParams(query)}`);
}

/** The device on the truck now, or null. */
export function getTruckDevice(truckId: string): Promise<DeviceMapping | null> {
  return api<DeviceMapping | null>(`/tracking/trucks/${truckId}/device`);
}

export function setTruckDevice(truckId: string, input: SetTruckDeviceInput): Promise<DeviceMapping | null> {
  return api<DeviceMapping | null>(`/tracking/trucks/${truckId}/device`, { method: 'PUT', body: json(input) });
}

export async function clearTruckDevice(truckId: string): Promise<void> {
  await api<void>(`/tracking/trucks/${truckId}/device`, { method: 'DELETE' });
}

export function listUnmappedDevices(query: UnmappedDeviceListQuery): Promise<Page<UnmappedDevice>> {
  return api<Page<UnmappedDevice>>(`/tracking/unmapped-devices?${toListParams(query)}`);
}
